// cspell:ignore unreviewed
/**
 * Who wrote a redaction-data asset — a person, or one of the pipelines.
 *
 * TWO MACHINE WRITERS NOW, not one: the upload pipeline's lexicon stub, and
 * the save the burn forces for a file released without review (VE-32440).
 * They are asked about identically — "did a PERSON do this?" — which is why
 * the predicate below is named for the question rather than for either
 * answer.
 *
 * ⚠️ A LEAF, and it has to stay one. Three unrelated places ask this question
 * and they must give the same answer about the same file: the editor's open
 * path (what `lastSavedAt` may claim), `watermarkAutoApply` (whether a
 * document has ever been saved), and the merged document's member classifier
 * (whether a file with no output is excludable or blocks the merge). The third
 * used to ask a looser question of its own — "does a redaction asset exist?" —
 * and refused to merge over documents nobody had opened. Keeping the predicate
 * importable without dragging a dependency chain behind it is what lets all
 * three share it; it lived in `services.ts` and pulling it into a GraphQL
 * helper brought the ingestion, mediaDetails and web-worker modules with it.
 */

/**
 * The writers that mark themselves. Everything else is a person.
 *
 * - `upload` — the ingestion pipeline's lexicon stub, written before anybody
 *   opens the file.
 * - `unreviewed-include` — the save the burn forces for a file the reviewer
 *   chose to release WITHOUT reviewing it (VE-32440). Not a person's save
 *   either: it records an empty redaction plan the reviewer never drew, and
 *   leaving it unmarked is what made that choice self-erasing — see
 *   `isMachineWrittenRedactionAsset`.
 */
export type RedactionAssetWriter = 'upload' | 'unreviewed-include';

/**
 * True when the asset was stamped by a pipeline rather than saved by a person.
 *
 * Absent/unknown values read as false — an unmarked asset is a save, which is
 * the safe direction: the worst case is an auto-apply that does not run, or a
 * merge that refuses, never work silently overwritten or a redacted file
 * silently dropped from a release.
 *
 * THE QUESTION IS "DID A PERSON SAVE THIS", not "did the upload write it".
 * The predicate was named for its only answer and every one of its callers
 * wanted the general question — so when the include-unworked burn started
 * forcing a save of its own (`unreviewed-include`), an unmarked asset made
 * all three of them report that a reviewer had worked on a file nobody had
 * opened. The file then stopped being offered by the dialog, the grey minus
 * went away, and every later pass burned it with no consent step at all: a
 * one-time decision laundered into a permanent fact. Asking the general
 * question is what keeps a new writer from doing that again.
 */
export const isMachineWrittenRedactionAsset = (details: unknown): boolean => {
  const writtenBy = readWrittenBy(details);
  return writtenBy === 'upload' || writtenBy === 'unreviewed-include';
};

/**
 * `details.writtenBy`, through a parse — the API returns JSONData as a
 * serialized string for some consumers (see `parseDetails` in
 * `documents/helpers.ts`, which has handled that since VE-20837).
 *
 * A SECOND COPY OF SIX LINES, DELIBERATELY. Importing the shared parse would
 * pull `documents/helpers.ts` in behind it, and this file exists precisely to
 * stay importable without that chain — see the header. The duplication is the
 * price of the leaf; reading the raw object was the alternative, and it got
 * the answer wrong in exactly the case that matters: an `unreviewed-include`
 * stub whose details arrived serialized read as a person's save, which clears
 * the row's "included as is" mark and reports an unreviewed release as an
 * ordinary redacted output. Unparseable reads as unknown, which reads as a
 * save — the same safe direction as everything else here.
 */
const readWrittenBy = (details: unknown): unknown => {
  let obj: unknown = details;
  if (typeof obj === 'string') {
    try {
      obj = JSON.parse(obj);
    } catch {
      return undefined;
    }
  }
  return typeof obj === 'object' && obj !== null
    ? (obj as { writtenBy?: unknown }).writtenBy
    : undefined;
};
